import React, { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabaseClient';
import { useSelector } from 'react-redux';
import { formatDistanceToNow, format } from 'date-fns';
import { Activity, Clock, CheckCircle2, AlertCircle, RefreshCw, Trash2, Plus, Edit } from 'lucide-react';

const ActivityTab = () => {

  const { user } = useSelector((state) => state.auth);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const fetchActivities = async () => {
      if (!user?.id) return;
      
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('activities')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(50);
          
        if (error) throw error;
        setActivities(data || []);
      } catch (err) {
        console.error("Error fetching activities:", err);
        setError("Failed to load activity history.");
      } finally {
        setLoading(false);
      }
    };
    
    fetchActivities();
  }, [user]);

  const getActionConfig = (type) => {
    const t = type?.toLowerCase() || '';
    if (t.includes('create') || t.includes('add') || t.includes('register')) {
      return { color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: Plus, label: type };
    }
    if (t.includes('update') || t.includes('edit')) {
      return { color: 'bg-primary/20 text-primary border-primary/30', icon: Edit, label: type };
    }
    if (t.includes('delete') || t.includes('remove') || t.includes('write-off') || t.includes('archived')) {
      return { color: 'bg-red-100 text-red-700 border-red-200', icon: Trash2, label: type };
    }
    if (t.includes('status') || t.includes('sync')) {
      return { color: 'bg-purple-100 text-purple-700 border-purple-200', icon: RefreshCw, label: type };
    }
    return { color: 'bg-muted text-foreground border-border', icon: Activity, label: type };
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
        <div className="px-8 py-6 border-b border-border bg-muted/20">
          <div className="flex items-center gap-3 mb-1">
            <div className="p-2 bg-indigo-100 text-indigo-700 rounded-lg">
              <Activity size={20} />
            </div>
            <h2 className="text-lg font-bold text-foreground">Activity Log</h2>
          </div>
          <p className="text-sm text-muted-foreground ml-11">A secure audit trail of the last 50 actions performed by your account.</p>
        </div>
        
        <div className="p-8">
          {loading ? (
             <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
               <RefreshCw className="w-8 h-8 animate-spin mb-4" />
               <p>Loading your activity history...</p>
             </div>
          ) : error ? (
            <div className="p-4 bg-red-50 text-red-700 rounded-lg flex items-center gap-2 border border-red-200">
              <AlertCircle size={20} />
              <p className="text-sm font-medium">{error}</p>
            </div>
          ) : activities.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-border rounded-xl bg-muted/10">
               <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-4">
                 <Clock className="w-8 h-8 text-muted-foreground" />
               </div>
               <h3 className="text-lg font-bold text-foreground mb-1">No Activity Found</h3>
               <p className="text-sm text-muted-foreground">You haven't performed any logged actions yet.</p>
            </div>
          ) : (
            <>
              <div className={`relative transition-all duration-300 ${isExpanded ? 'max-h-[600px] overflow-y-auto pr-4 scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent' : ''}`}>
                <div className="relative border-l-2 border-border ml-4 space-y-8 pb-4 mt-2">
                  {(isExpanded ? activities : activities.slice(0, 5)).map((activity, index) => {
                const config = getActionConfig(activity.type);
                const Icon = config.icon;
                
                // Format relative time: "2 hours ago"
                const relativeTime = formatDistanceToNow(new Date(activity.created_at), { addSuffix: true });
                // Format absolute time: "Mar 12, 2026 3:00 PM"
                const exactTime = format(new Date(activity.created_at), 'MMM d, yyyy h:mm a');
                
                return (
                  <div key={activity.id || index} className="relative pl-8 group">
                    {/* Timeline Node */}
                    <div className="absolute -left-[17px] top-1 w-8 h-8 bg-card rounded-full border-4 border-border flex items-center justify-center group-hover:border-indigo-100 transition-colors z-10">
                      <div className="w-2.5 h-2.5 bg-gray-300 rounded-full group-hover:bg-indigo-500 transition-colors" />
                    </div>
                    
                    {/* Content Box */}
                    <div className="bg-card border border-border shadow-sm rounded-xl p-5 hover:shadow-md transition-shadow group-hover:border-indigo-100">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-bold border ${config.color} uppercase tracking-wider`}>
                            <Icon size={12} />
                            {config.label}
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground font-medium flex items-center gap-1.5" title={exactTime}>
                          <Clock size={12} />
                          {relativeTime}
                        </div>
                      </div>
                      
                      <p className="text-sm text-foreground font-medium">
                        {activity.description}
                      </p>
                      
                      {activity.asset_id && (
                        <div className="mt-4 pt-4 border-t border-border/50 flex items-center gap-2">
                          <span className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider">Asset Ref</span>
                          <span className="text-[11px] font-mono bg-muted/50 text-foreground px-2 py-0.5 rounded border border-border">
                            {activity.asset_id}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
                </div>
              </div>
              
              {activities.length > 5 && (
                <div className="mt-8 flex justify-center border-t border-dashed border-border pt-6">
                  <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="px-5 py-2.5 bg-muted/50 hover:bg-muted text-foreground text-sm font-semibold rounded-xl border border-border transition-colors flex items-center gap-2"
                  >
                    {isExpanded ? 'Show less' : `View all ${activities.length} activities`}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivityTab;
